import type { FulfillmentEvent, FulfillmentOrderLine } from '@/infrastructure/FulfillmentAdminClient';

/**
 * Per-line cancel instructions (SSD-7533, SSD-7771). Actual stock movements win, then structured line choices,
 * then fixed-format historical goods notes, then the order-wide default. A historical Return records intent only:
 * unlike WRITE_OFF it is never evidence that stock was removed.
 */
export type CancelledGoodsKind =
  | 'KEEP'
  | 'WRITE_OFF'
  | 'RETURN_TO_SELLER'
  | 'NOT_PURCHASED'
  // [SSD-7906] Bought but the seller never shipped it; only an order cancel's per-line answer carries it.
  | 'NOT_SHIPPED_BY_SELLER'
  | 'NOT_RECORDED';

export interface CancelledGoodsLine {
  lineNo: number;
  sku: string;
  kind: CancelledGoodsKind;
  /** Units written off, or moved when kept on a named shelf. */
  qty?: number;
  /** The shelf the written-off units left, or the shelf kept units were moved to. */
  location?: string;
  /** On KEEP or RETURN_TO_SELLER: whether the event log shows goods received before the cancel. */
  arrivalOnRecord?: boolean;
  /** Explicit per-line choice, rather than the historical order-wide default. */
  explicitChoice?: boolean;
  /** A WRITE_OFF was the stock effect of an explicit Return to seller choice. */
  returnToSeller?: boolean;
  /** A KEEP answer is for a line fulfilled from existing inventory. */
  fromInventory?: boolean;
  /** The cancel this line's answer came from; lines can come from different cancels. */
  by?: string | null;
  at?: string;
  reason?: string | null;
}

type Payload = Record<string, unknown>;
type Answer = Pick<
  CancelledGoodsLine,
  'kind' | 'qty' | 'location' | 'explicitChoice' | 'returnToSeller' | 'fromInventory'
>;

const payloadOf = (event: FulfillmentEvent): Payload => {
  if (event.payload && typeof event.payload === 'object') {
    return event.payload as Payload;
  }
  if (typeof event.payload === 'string') {
    try {
      const parsed: unknown = JSON.parse(event.payload);
      return parsed && typeof parsed === 'object' ? (parsed as Payload) : {};
    } catch {
      return {};
    }
  }
  return {};
};

const numberOf = (value: unknown): number | undefined => (typeof value === 'number' ? value : undefined);
const textOf = (value: unknown): string | undefined =>
  typeof value === 'string' && value.trim() !== '' ? value.trim() : undefined;
const recordsOf = (value: unknown): Payload[] =>
  Array.isArray(value) ? value.filter((item): item is Payload => item !== null && typeof item === 'object') : [];
const timeOf = (event: FulfillmentEvent) => new Date(event.occurredAt).getTime();
const newestFirst = (a: FulfillmentEvent, b: FulfillmentEvent) => timeOf(b) - timeOf(a);

/** Unknown or missing values are not instructions. */
const answerKind = (value: unknown): CancelledGoodsKind | undefined => {
  switch (value) {
    case 'KEEP_IN_STOCK':
      return 'KEEP';
    case 'WRITE_OFF':
      return 'WRITE_OFF';
    case 'RETURN_TO_SELLER':
      return 'RETURN_TO_SELLER';
    case 'NOT_PURCHASED':
      return 'NOT_PURCHASED';
    case 'NOT_SHIPPED_BY_SELLER':
      return 'NOT_SHIPPED_BY_SELLER';
    default:
      return undefined;
  }
};

/** Historical comment format only: never infer a stock write-off from a return sentence. */
const legacyAnswer = (payload: Payload, lineNo: number): Answer | undefined => {
  const request = recordsOf([payload['request']])[0];
  const note = textOf(request?.['goodsNote']);
  for (const clause of note?.split(';') ?? []) {
    const match =
      /^line ([1-9]\d*) \S+ x\d+: (return to seller|keep in stock|back to stock \(existing inventory\))$/.exec(
        clause.trim(),
      );
    if (match && Number(match[1]) === lineNo) {
      return {
        kind: match[2] === 'return to seller' ? 'RETURN_TO_SELLER' : 'KEEP',
        explicitChoice: true,
        ...(match[2] === 'back to stock (existing inventory)' && { fromInventory: true }),
      };
    }
  }
  return undefined;
};

const writeOff = (row: Payload | undefined, qtyKey: string): Answer => {
  const qty = row ? numberOf(row[qtyKey]) : undefined;
  const location = row ? textOf(row['locationCode']) : undefined;
  return { kind: 'WRITE_OFF', ...(qty !== undefined && { qty }), ...(location && { location }) };
};

const keptOn = (stored: Payload): Answer => {
  const qty = numberOf(stored['qty']);
  const location = textOf(stored['toLocation']);
  return { kind: 'KEEP', ...(qty !== undefined && { qty }), ...(location && { location }) };
};

/** A single-line cancel's answer for its own line. */
const lineCancelAnswer = (payload: Payload): Answer | undefined => {
  const stored = recordsOf([payload['stored']])[0];
  if (numberOf(payload['writtenOffQty']) !== undefined) {
    return writeOff(payload, 'writtenOffQty');
  }
  if (stored) {
    return keptOn(stored);
  }
  const kind = answerKind(payload['receivedGoods']);
  return kind === 'WRITE_OFF' ? writeOff(payload, 'writtenOffQty') : kind ? { kind } : undefined;
};

/** An order cancel's answer for one line it covered: the line's own entry wins over the order-wide one. */
const orderCancelAnswer = (payload: Payload, line: FulfillmentOrderLine): Answer | undefined => {
  const { lineNo } = line;
  const named = recordsOf(payload['lines']).find((row) => numberOf(row['lineNo']) === lineNo);
  const namedKind = answerKind(named?.['receivedGoods']);
  const writtenOff = recordsOf(payload['writtenOff']).find((row) => numberOf(row['lineNo']) === lineNo);
  if (writtenOff) {
    return { ...writeOff(writtenOff, 'qty'), ...(namedKind === 'WRITE_OFF' && { returnToSeller: true }) };
  }
  const stored = recordsOf(payload['stored']).find((row) => numberOf(row['lineNo']) === lineNo);
  if (stored) {
    return keptOn(stored);
  }
  if (namedKind) {
    return {
      kind: namedKind,
      explicitChoice: true,
      ...(namedKind === 'WRITE_OFF' && { returnToSeller: true }),
      ...(namedKind === 'KEEP' && line.sourcingRoute === 'EXISTING_INVENTORY' && { fromInventory: true }),
    };
  }
  if (!Array.isArray(payload['lines']) || payload['lines'].length === 0) {
    const legacy = legacyAnswer(payload, lineNo);
    if (legacy) {
      return legacy;
    }
  }
  const orderKind = answerKind(payload['receivedGoods']);
  return orderKind ? { kind: orderKind } : undefined;
};

/** Units of the line on the event log just before `at`: arrivals less arrivals reversed. */
const receivedBefore = (events: FulfillmentEvent[], lineNo: number, at: number): number =>
  events
    .filter((event) => timeOf(event) <= at)
    .reduce((total, event) => {
      const payload = payloadOf(event);
      if (numberOf(payload['lineNo']) !== lineNo) {
        return total;
      }
      const qty = numberOf(payload['qty']) ?? 0;
      if (event.operation === 'ADMIN_INBOUND') {
        return total + qty;
      }
      if (event.operation === 'ADMIN_INBOUND_CANCEL') {
        return total - qty;
      }
      return total;
    }, 0);

/**
 * Per line, what the cancel record says to do with the goods. A line's own single-line cancel is its answer: an
 * order cancel written later only covers the lines still standing then, and its payload does not list them.
 */
export const cancelledGoodsOf = (events: FulfillmentEvent[], lines: FulfillmentOrderLine[]): CancelledGoodsLine[] => {
  const lineCancels = events.filter((event) => event.operation === 'ADMIN_LINE_CANCEL').sort(newestFirst);
  const orderCancels = events.filter((event) => event.operation === 'ORDER_CANCELLED').sort(newestFirst);

  return lines.map((line): CancelledGoodsLine => {
    const own = lineCancels.find((event) => numberOf(payloadOf(event)['lineNo']) === line.lineNo);
    const event = own ?? orderCancels[0];
    if (!event) {
      return { lineNo: line.lineNo, sku: line.sku, kind: 'NOT_RECORDED' };
    }
    const payload = payloadOf(event);
    const answer = own ? lineCancelAnswer(payload) : orderCancelAnswer(payload, line);
    const source = { by: event.actor || null, at: event.occurredAt, reason: textOf(payload['reason']) ?? null };
    if (!answer) {
      return { lineNo: line.lineNo, sku: line.sku, kind: 'NOT_RECORDED', ...source };
    }
    const flag =
      (answer.kind === 'KEEP' || answer.kind === 'RETURN_TO_SELLER') && answer.location === undefined
        ? { arrivalOnRecord: receivedBefore(events, line.lineNo, timeOf(event)) > 0 }
        : {};
    return { lineNo: line.lineNo, sku: line.sku, ...answer, ...flag, ...source };
  });
};
